import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import { GlobalConfirmDialog } from "components/DesignSystem/sharedUiPatterns";
import { SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { designType } from "services/constants/designTokens";
import { SelectField } from "pages/PayRoll/PayInputs/Shared/fieldInputs";
import { activateSite, deactivateSite, deleteSite, getSiteOptions, moveSiteEmployees } from "./sitesApi";

// Site actions from the Details drawer's footer: Activate / Deactivate /
// Delete confirms and Move employees. action = { type, site }. A blocked
// Deactivate shows the server's message with a "Move employees" button.

const RED = "#DC2626";

const CONFIRM = {
  deactivate: {
    title: "Deactivate site",
    confirmLabel: "Deactivate",
    loadingLabel: "Deactivating...",
    danger: true,
    text: (site) => `Deactivate ${site.name}? It will no longer appear in the onboarding site list.`,
    run: deactivateSite,
  },
  activate: {
    title: "Activate site",
    confirmLabel: "Activate",
    loadingLabel: "Activating...",
    danger: false,
    text: (site) => `Activate ${site.name}? It will appear in the onboarding site list again.`,
    run: activateSite,
  },
  delete: {
    title: "Delete site",
    confirmLabel: "Delete",
    loadingLabel: "Deleting...",
    danger: true,
    text: (site) => `Delete ${site.name}? This cannot be undone.`,
    run: deleteSite,
  },
};

const ConfirmSiteAction = ({ action, domain, onChange, onClose, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const config = CONFIRM[action.type];
  const { site } = action;

  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const result = await config.run({ domain, id: site.id });
      onDone({
        message: result?.message || (action.type === "delete" ? `${site.name} deleted.` : undefined),
        deleted: action.type === "delete",
      });
    } catch (runError) {
      setError(runError.message || "Could not complete the action.");
    } finally {
      setBusy(false);
    }
  };

  // Blocked because employees work here: offer the Move modal.
  const blocked = Boolean(error) && /employees/i.test(error) && action.type !== "activate";

  return (
    <GlobalConfirmDialog
      open
      title={config.title}
      onCancel={() => (busy ? null : onClose())}
      onConfirm={confirm}
      confirmLabel={config.confirmLabel}
      cancelLabel="Cancel"
      loading={busy}
      loadingLabel={config.loadingLabel}
      danger={config.danger}
      confirmDisabled={blocked}
      maxWidth="sm"
    >
      <Mui.Typography sx={designType.tableBodyCell}>{config.text(site)}</Mui.Typography>
      {error ? (
        <Mui.Stack spacing={1} sx={{ mt: 1.5 }}>
          <Mui.Typography role="alert" sx={{ ...designType.tableBodyCell, color: RED }}>
            {error}
          </Mui.Typography>
          {blocked ? (
            <Mui.Box>
              <SecondaryButton onClick={() => onChange({ type: "move", site })}>Move employees</SecondaryButton>
            </Mui.Box>
          ) : null}
        </Mui.Stack>
      ) : null}
    </GlobalConfirmDialog>
  );
};

export const MoveEmployeesModal = ({ site, domain, onClose, onDone }) => {
  const [options, setOptions] = useState({ loading: true, rows: [] });
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    getSiteOptions({ domain, signal: controller.signal })
      .then((rows) =>
        setOptions({
          loading: false,
          rows: rows.filter((row) => row.id !== site.id).map((row) => ({ value: String(row.id), label: `${row.name} (${row.code})` })),
        }),
      )
      .catch((loadError) => {
        if (controller.signal.aborted) return;
        setOptions({ loading: false, rows: [] });
        setError(loadError.message || "Could not load the sites.");
      });
    return () => controller.abort();
  }, [domain, site.id]);

  const confirm = async () => {
    if (!target || busy) return;
    setBusy(true);
    setError("");
    try {
      const { moved } = await moveSiteEmployees({ domain, id: site.id, targetSiteId: Number(target) });
      onDone({ message: `${moved} employees moved` });
    } catch (moveError) {
      setError(moveError.message || "Could not move the employees.");
    } finally {
      setBusy(false);
    }
  };

  const count = Number(site.employee_count || 0);
  return (
    <GlobalConfirmDialog
      open
      title="Move employees"
      onCancel={() => (busy ? null : onClose())}
      onConfirm={confirm}
      confirmLabel="Move"
      cancelLabel="Cancel"
      loading={busy}
      loadingLabel="Moving..."
      confirmDisabled={!target}
      maxWidth="sm"
    >
      <Mui.Stack spacing={1.5}>
        <Mui.Typography sx={designType.tableBodyCell}>
          {count} employees will be moved from {site.name}. Inactive employees linked to this site move too.
        </Mui.Typography>
        <Mui.Box>
          <Mui.Typography sx={{ ...designType.captionText, mb: 0.5 }}>Move to</Mui.Typography>
          <SelectField
            value={target}
            onChange={setTarget}
            options={options.rows}
            placeholder={options.loading ? "Loading sites..." : options.rows.length ? "Select an active site" : "No other active site"}
            disabled={options.loading}
          />
        </Mui.Box>
        {error ? (
          <Mui.Typography role="alert" sx={{ ...designType.tableBodyCell, color: RED }}>
            {error}
          </Mui.Typography>
        ) : null}
      </Mui.Stack>
    </GlobalConfirmDialog>
  );
};

export const SiteActionModals = ({ action, domain, onChange, onClose, onDone }) => {
  if (!action?.site) return null;
  if (action.type === "move") {
    return <MoveEmployeesModal key={`move-${action.site.id}`} site={action.site} domain={domain} onClose={onClose} onDone={onDone} />;
  }
  if (!CONFIRM[action.type]) return null;
  return <ConfirmSiteAction key={`${action.type}-${action.site.id}`} action={action} domain={domain} onChange={onChange} onClose={onClose} onDone={onDone} />;
};
